import { ApiClient, apiClient } from '../apiClient';
import type { models_CustomersListResponse } from '../../api/generated/models/models_CustomersListResponse';
import type { models_CustomerResponse } from '../../api/generated/models/models_CustomerResponse';
import type { models_CreateCustomerRequest } from '../../api/generated/models/models_CreateCustomerRequest';
import type { models_UpdateCustomerRequest } from '../../api/generated/models/models_UpdateCustomerRequest';
import type { models_CustomerDeleteResponse } from '../../api/generated/models/models_CustomerDeleteResponse';

export class OrganizedCustomersService {
  private apiClient: ApiClient;

  constructor(apiClient: ApiClient) {
    this.apiClient = apiClient;
  }

  /**
   * List customers for an organization
   */
  async listCustomers(
    organizationSlug: string,
    params?: {
      page?: number;
      page_size?: number;
      search?: string;
      email?: string;
      name?: string;
    }
  ): Promise<models_CustomersListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.email) queryParams.append('email', params.email);
    if (params?.name) queryParams.append('name', params.name);

    const query = queryParams.toString();
    const url = `/organizers/${organizationSlug}/customers${query ? `?${query}` : ''}`;

    return this.apiClient.get<models_CustomersListResponse>(url);
  }

  /**
   * Get a specific customer by ID
   */
  async getCustomer(
    organizationSlug: string,
    customerId: string
  ): Promise<models_CustomerResponse> {
    const url = `/organizers/${organizationSlug}/customers/${customerId}`;
    return this.apiClient.get<models_CustomerResponse>(url);
  }

  /**
   * Create a new customer
   */
  async createCustomer(
    organizationSlug: string,
    customerData: models_CreateCustomerRequest
  ): Promise<models_CustomerResponse> {
    const url = `/organizers/${organizationSlug}/customers`;
    return this.apiClient.post<models_CustomerResponse>(url, customerData);
  }

  /**
   * Update an existing customer
   */
  async updateCustomer(
    organizationSlug: string,
    customerId: string,
    customerData: models_UpdateCustomerRequest
  ): Promise<models_CustomerResponse> {
    const url = `/organizers/${organizationSlug}/customers/${customerId}`;
    return this.apiClient.put<models_CustomerResponse>(url, customerData);
  }

  /**
   * Delete a customer
   */
  async deleteCustomer(
    organizationSlug: string,
    customerId: string
  ): Promise<models_CustomerDeleteResponse> {
    const url = `/organizers/${organizationSlug}/customers/${customerId}`;
    return this.apiClient.delete<models_CustomerDeleteResponse>(url);
  }
}

// Create a singleton instance
export const organizedCustomersService = new OrganizedCustomersService(apiClient);